import { motion } from 'framer-motion';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, rem } from '@bonusly/design-system';
import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { Bizy } from '../../../../components/bizy';
import { useBizyAskClick } from '../../../../components/bizy-side-panel/hooks/use-bizy-ask-click';
import { useBizyNotificationAttention } from '../../../../components/bizy-side-panel/hooks/use-has-bizy-notification';
import { useBizySidePanelStore } from '../../../../components/bizy-side-panel/store/use-bizy-side-panel-store';

export const AskBizyButton: FC = () => {
	const { t } = useTranslation('vertical_navbar');
	const isEnabled = useIsBizyEnabledForUser();
	const isOpen = useBizySidePanelStore((s) => s.isOpen);
	const { shouldSurface: shouldSurfaceBizyNotification, unreadCount: surfacedUnreadCount } = useBizyNotificationAttention();
	const handleClick = useBizyAskClick();

	if (!isEnabled) return null;

	return (
		<motion.div
			initial={false}
			animate={{ height: isOpen ? 0 : 'auto', opacity: isOpen ? 0 : 1 }}
			transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
			style={{ overflow: 'hidden' }}
		>
			<Box pt={3}>
				<Button
					colorScheme="secondary"
					variant="ghost"
					borderRadius="sm"
					h="auto"
					w="100%"
					py={1}
					px={2}
					fontSize={50}
					fontWeight="medium"
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					gap={2}
					onClick={handleClick}
					data-testid="ask-bizy-button"
					aria-label={shouldSurfaceBizyNotification ? t('admin_menu.ask_bizy_with_notification', 'Ask Bizy — you have a new message') : undefined}
					_hover={{ bg: 'secondary.bgAlpha' }}
				>
					<Box display="inline-flex" alignItems="center" gap={2}>
						<motion.div
							animate={
								shouldSurfaceBizyNotification
									? {
											rotate: [0, -12, 12, -12, 12, 0],
											scale: [1, 1.08, 1.08, 1.08, 1.08, 1],
										}
									: { rotate: 0, scale: 1 }
							}
							transition={shouldSurfaceBizyNotification ? { duration: 1.4, repeat: Infinity, repeatDelay: 1.6, ease: 'easeInOut' } : { duration: 0.2 }}
							style={{ display: 'flex', transformOrigin: 'bottom center' }}
						>
							<Bizy state={shouldSurfaceBizyNotification ? 'waving' : 'curious'} size={32} />
						</motion.div>
						{t('admin_menu.ask_bizy')}
					</Box>
					{shouldSurfaceBizyNotification && (
						// Styled like the Rewards "+N" points pill, in the People
						// Browser's unread blue. The count is unread *people*, which
						// decrements as each is viewed, so it's always a meaningful
						// number.
						<Box bgColor="primary.bgAlpha" fontWeight="semibold" fontSize={1} color="primary.fg" borderRadius="full" px={rem(6)} py={rem(3)}>
							{surfacedUnreadCount > 9 ? '9+' : surfacedUnreadCount}
						</Box>
					)}
				</Button>
			</Box>
		</motion.div>
	);
};
